import { z } from 'zod';
import { AllergenSchema } from './food.js';

// T-373 — Schemi del dominio "menù pubblico" (categorie + piatti) gestibili
// in-app dal ristoratore. Le tabelle esistono da T-020 (`menu_schema.sql`);
// qui aggiungiamo la validazione Zod al boundary (l'editor client Zod-parsa
// ciò che legge/scrive), finora assente (c'era solo `menu-rotation.ts`).
//
// NB: la pagina pubblica (`landing/functions/menu/[slug].ts`) NON usa questi
// schemi (gira su Cloudflare, legge diretto); sono per l'app mobile/web.

/** Sezione della carta: cibo o bar. Guida l'ordine di render pubblico. */
export const MenuSectionSchema = z.enum(['food', 'bar']);
export type MenuSection = z.infer<typeof MenuSectionSchema>;

/** Flag dietetici evidenziati come pill accanto al piatto (Gold Standard §7). */
export const DietaryFlagSchema = z.enum(['vegetarian', 'vegan', 'gluten_free']);
export type DietaryFlag = z.infer<typeof DietaryFlagSchema>;

export const DIETARY_LABELS_IT: Record<DietaryFlag, string> = {
  vegetarian: 'Vegetariano',
  vegan: 'Vegano',
  gluten_free: 'Senza glutine',
};

/**
 * Traduzioni per-lingua. `long_description` è la descrizione estesa mostrata
 * nel dettaglio del piatto sul menù pubblico. Struttura libera ma tipizzata
 * sui campi noti così da non perdere dati sconosciuti in fase di parse.
 */
export const MenuTranslationsSchema = z
  .record(
    z.string(),
    z
      .object({
        name: z.string().optional(),
        description: z.string().optional(),
        long_description: z.string().optional(),
      })
      .passthrough(),
  )
  .default({});
export type MenuTranslations = z.infer<typeof MenuTranslationsSchema>;

/** Categoria del menù (es. Antipasti, Burgers, Gelato). */
export const MenuCategorySchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  name: z.string().min(1),
  icon: z.string().nullable().default(null),
  sort_order: z.number().int().default(0),
  visible: z.boolean().default(true),
  section: MenuSectionSchema.default('food'),
  name_en: z.string().nullable().default(null),
  translations: MenuTranslationsSchema,
  version: z.number().int().default(1),
});
export type MenuCategory = z.infer<typeof MenuCategorySchema>;

/** Piatto/voce del menù. */
export const MenuItemSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  category_id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().default(''),
  price_cents: z.number().int().nonnegative().nullable().default(null),
  price_unit: z.string().nullable().default(null),
  allergens: z.array(AllergenSchema).default([]),
  dietary_flags: z.array(DietaryFlagSchema).default([]),
  station: z.string().nullable().default(null),
  sort_order: z.number().int().default(0),
  visible: z.boolean().default(true),
  out_of_stock: z.boolean().default(false),
  photo_url: z.string().nullable().default(null),
  name_en: z.string().nullable().default(null),
  description_en: z.string().nullable().default(null),
  translations: MenuTranslationsSchema,
  version: z.number().int().default(1),
});
export type MenuItem = z.infer<typeof MenuItemSchema>;

// ── Input schemas (create/update dall'editor) ────────────────────────────────
// Il client compila un sottoinsieme; tenant_id/business_id/version li mette il
// layer dati. Questi schemi validano ciò che l'utente inserisce.

export const MenuCategoryInputSchema = MenuCategorySchema.pick({
  name: true,
  icon: true,
  section: true,
  visible: true,
  sort_order: true,
}).partial({ icon: true, section: true, visible: true, sort_order: true });
export type MenuCategoryInput = z.infer<typeof MenuCategoryInputSchema>;

export const MenuItemInputSchema = MenuItemSchema.pick({
  category_id: true,
  name: true,
  description: true,
  price_cents: true,
  price_unit: true,
  allergens: true,
  dietary_flags: true,
  visible: true,
  out_of_stock: true,
  photo_url: true,
  translations: true,
  sort_order: true,
}).partial({
  description: true,
  price_cents: true,
  price_unit: true,
  allergens: true,
  dietary_flags: true,
  visible: true,
  out_of_stock: true,
  photo_url: true,
  translations: true,
  sort_order: true,
});
export type MenuItemInput = z.infer<typeof MenuItemInputSchema>;

/** Ruoli abilitati a modificare il menù dall'app (rispecchia la RLS). */
export const MENU_EDITOR_ROLES = ['owner', 'consulente', 'ristoratore'] as const;
export function canEditMenu(role: string | null | undefined): boolean {
  return !!role && (MENU_EDITOR_ROLES as readonly string[]).includes(role);
}
